import { useEffect, useRef, useState } from 'react';
import { RouteComponentProps, useLocation, useNavigate } from '@reach/router';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { axisPaths } from '../AxisConstants';
import { AxisOptionSelector } from '../components/AxisOptionSelector';
import {
  CLIENT_TYPE_IDS,
  CLIENT_TYPE_OPTIONS,
  COMMERCIAL_INDUSTRY_OPTIONS,
  AxisIndustryId,
  AxisSubIndustryId,
  ClientType,
  LWB_INDUSTRY_OPTIONS,
  PERSONAL_INDUSTRY_OPTIONS,
  commercialIndustryRequiresSubIndustry,
  getCommercialSubIndustryOptions,
  isContractorsIndustry,
  isExtendedApplicationContractorSubIndustry,
  PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS,
  resolveNewClientFormInternalName,
} from '../axisNewClientConstants';
import { setAxisStructuredData } from '../../../store/axisStore';
import { AXIS_NEW_CLIENT_INQUIRY_OPTION } from '../axisStructuredDataConstants';
import { parseAxisNewClientDeepLinkParams } from '../axisDeepLinkParams';
import { AxisLogo } from '../components/AxisLogo';
import { scrollToElement } from '../scrollToElement';
import { ArchitectsForms, EngineersForms } from './ContinueApplicationForms';
import { NewClientInquiryForm } from './NewClientInquiryForm';

type NewClientStep = 'clientType' | 'industry' | 'subIndustry' | 'form';

export const NewClientPage = (_props: RouteComponentProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState<NewClientStep>('clientType');
  const [clientType, setClientType] = useState<ClientType | null>(null);
  const [industryId, setIndustryId] = useState<AxisIndustryId | null>(null);
  const [subIndustryId, setSubIndustryId] = useState<AxisSubIndustryId | null>(
    null,
  );
  const [submissionStarted, setSubmissionStarted] = useState(false);

  const industrySectionRef = useRef<HTMLDivElement>(null);
  const subIndustrySectionRef = useRef<HTMLDivElement>(null);
  const formSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const deepLinkState = parseAxisNewClientDeepLinkParams(
      new URLSearchParams(location.search),
    );

    setStep(deepLinkState?.step ?? 'clientType');
    setClientType(deepLinkState?.clientType ?? null);
    setIndustryId(deepLinkState?.industryId ?? null);
    setSubIndustryId(deepLinkState?.subIndustryId ?? null);

    dispatch(
      setAxisStructuredData({
        ...AXIS_NEW_CLIENT_INQUIRY_OPTION.structuredData,
        type:
          deepLinkState?.clientType ??
          AXIS_NEW_CLIENT_INQUIRY_OPTION.structuredData.type,
        industry: deepLinkState?.industryId ?? '',
        subIndustry: deepLinkState?.subIndustryId ?? '',
      }),
    );
  }, [dispatch, location.search]);

  const isCommercialLikeType = clientType === CLIENT_TYPE_IDS.commercial;

  const industryOptions = isCommercialLikeType
    ? COMMERCIAL_INDUSTRY_OPTIONS
    : clientType === CLIENT_TYPE_IDS.personal
    ? PERSONAL_INDUSTRY_OPTIONS
    : clientType === CLIENT_TYPE_IDS.lwb
    ? LWB_INDUSTRY_OPTIONS
    : [];

  const subIndustryOptions =
    isCommercialLikeType && industryId
      ? getCommercialSubIndustryOptions(industryId)
      : [];

  const showSubIndustrySection =
    industryId !== null &&
    isCommercialLikeType &&
    commercialIndustryRequiresSubIndustry(industryId);

  const formInternalName = clientType
    ? resolveNewClientFormInternalName(clientType, industryId)
    : '';

  const industryQuestion = isCommercialLikeType
    ? 'What industry best describes your business?'
    : 'What type of coverage are you looking for?';

  const handleClientTypeSelect = (id: ClientType) => {
    setClientType(id);
    setIndustryId(null);
    setSubIndustryId(null);
    dispatch(
      setAxisStructuredData({
        type: id,
        industry: '',
        subIndustry: '',
      }),
    );
    setStep('industry');
  };

  const handleIndustrySelect = (id: AxisIndustryId) => {
    setIndustryId(id);
    setSubIndustryId(null);
    dispatch(
      setAxisStructuredData({
        industry: id,
        subIndustry: '',
      }),
    );
    if (isCommercialLikeType && commercialIndustryRequiresSubIndustry(id)) {
      setStep('subIndustry');
      return;
    }
    setStep('form');
  };

  const handleSubIndustrySelect = (id: AxisSubIndustryId) => {
    setSubIndustryId(id);
    dispatch(
      setAxisStructuredData({
        subIndustry: id,
      }),
    );
    setStep('form');
  };

  const isEngineersSelected =
    subIndustryId === PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS.engineers;
  const isArchitectsSelected =
    subIndustryId === PROFESSIONAL_FINANCIAL_SUB_INDUSTRY_IDS.architects;

  const isContractorFlow =
    clientType === CLIENT_TYPE_IDS.commercial &&
    industryId !== null &&
    isContractorsIndustry(industryId) &&
    isExtendedApplicationContractorSubIndustry(subIndustryId);

  const handleFormBack = () => {
    if (showSubIndustrySection) {
      setSubIndustryId(null);
      setStep('subIndustry');
      return;
    }
    setIndustryId(null);
    setStep('industry');
  };

  return (
    <Outer>
      <Card>
        <AxisLogo placement='corner' />
        <PageHeading>New Client</PageHeading>

        <StepsContainer>
          <StepCollapse in={!submissionStarted} timeout='auto'>
            <StepSection>
              <AxisOptionSelector
                question='What type of insurance are you looking for?'
                options={CLIENT_TYPE_OPTIONS}
                selectedId={clientType}
                onSelect={handleClientTypeSelect}
              />
            </StepSection>
          </StepCollapse>

          <StepCollapse
            in={Boolean(clientType) && !submissionStarted}
            timeout='auto'
            onEntered={() => scrollToElement(industrySectionRef.current)}
          >
            <StepSection ref={industrySectionRef}>
              <AxisOptionSelector
                question={industryQuestion}
                options={industryOptions}
                selectedId={industryId}
                onSelect={handleIndustrySelect}
              />
            </StepSection>
          </StepCollapse>

          <StepCollapse
            in={showSubIndustrySection && !submissionStarted}
            timeout='auto'
            onEntered={() => scrollToElement(subIndustrySectionRef.current)}
          >
            <StepSection ref={subIndustrySectionRef}>
              <AxisOptionSelector
                question='Please select a sub-industry'
                options={subIndustryOptions}
                selectedId={subIndustryId}
                onSelect={handleSubIndustrySelect}
              />
            </StepSection>
          </StepCollapse>

          <StepCollapse
            in={step === 'form' && Boolean(formInternalName)}
            timeout='auto'
            mountOnEnter
            unmountOnExit
            onEntered={() => scrollToElement(formSectionRef.current)}
          >
            <div ref={formSectionRef}>
              {isEngineersSelected ? (
                <EngineersForms
                  subIndustryId={subIndustryId}
                  onBack={handleFormBack}
                />
              ) : isArchitectsSelected ? (
                <ArchitectsForms
                  subIndustryId={subIndustryId}
                  onBack={handleFormBack}
                />
              ) : (
                <NewClientInquiryForm
                  key={formInternalName}
                  formInternalName={formInternalName}
                  isContractorFlow={isContractorFlow}
                  subIndustryId={subIndustryId}
                  submissionStarted={submissionStarted}
                  setSubmissionStarted={setSubmissionStarted}
                  onBack={handleFormBack}
                />
              )}
            </div>
          </StepCollapse>
        </StepsContainer>
        {step !== 'form' ? (
          <BackIndustrySelectionButton
            type='button'
            onClick={() => navigate(axisPaths.welcome.url)}
          >
            Back
          </BackIndustrySelectionButton>
        ) : null}
      </Card>
    </Outer>
  );
};

const Outer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '50vh',
  padding: theme.spacing(4),
}));

const Card = styled(Paper)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: 920,
  padding: theme.spacing(9, 5, 5),
  textAlign: 'center',
}));

const PageHeading = styled(Typography)(({ theme }) => ({
  ...theme.typography.h4,
  marginBottom: theme.spacing(3),
}));

const StepsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const StepCollapse = styled(Collapse)({
  width: '100%',
});

const StepSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const BackIndustrySelectionButton = styled(Button)(({ theme }) => ({
  ...theme.typography.button,
  display: 'flex',
  marginLeft: 'auto',
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: 'transparent',
  padding: `${theme.spacing(0.75)} ${theme.spacing(2.5)}`,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
  },
}));
